import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type ChangeEvent,
  type ReactNode,
} from 'react'
import { faChevronLeft } from '@fortawesome/pro-regular-svg-icons'
import { AlertDialog, Badge, Button, Text, Textarea } from '@/ui'
import { TextColors, TypographyStyles } from '@/styles'
import { fetchMessageThread } from '../../adviser-chat.actions'
import { STATUS_COLOR, STATUS_LABEL } from '../../lib/messageStatus'
import { postTicketCommentPrompt } from '../../lib/messageTabs'
import { useAttachmentsWithAnalytics } from '../../hooks/useAttachmentsWithAnalytics'
import { useHavenMessagesView } from '../../hooks/useHavenMessagesView'
import { AttachmentDropZone } from '../AttachmentDropZone'
import { UploadingHint } from '../UploadingHint'
import type { AttachmentRef } from '../../lib/attachments.types'
import type { MessageThreadDetail } from '../../lib/types'
import { ThreadTimeline } from './ThreadTimeline'
import { formatShortDateTimeWithZone } from '@/utils/dateHelpers'

const COMPOSER_MAX_HEIGHT_PX = 160

export type SendMessage = (
  message: string,
  options?: {
    pendingAttachments?: AttachmentRef[]
    attachmentFiles?: File[]
  }
) => void

interface ThreadViewProps {
  ticketId: number
  onBack: () => void
  onSend?: SendMessage
  sendDisabled?: boolean
}

interface ReplyComposerProps {
  ticketId: number
  onSend: SendMessage
  onSent: () => void
  onCancel: () => void
  disabled?: boolean
}

const ReplyComposer = ({
  ticketId,
  onSend,
  onSent,
  onCancel,
  disabled = false,
}: ReplyComposerProps) => {
  const [body, setBody] = useState('')
  const [confirmingDiscard, setConfirmingDiscard] = useState(false)
  const uploadingHintId = useId()
  const attachmentsEnabled = useHavenMessagesView()
  const {
    items,
    add,
    retry,
    cancel,
    remove,
    clear,
    refs,
    uploadedFiles,
    isUploading,
    isFull,
    trackSubmitted,
  } = useAttachmentsWithAnalytics()

  const hasDraft = body.trim() !== '' || items.length > 0

  const discardDraft = (): void => {
    clear()
    setBody('')
    setConfirmingDiscard(false)
    onCancel()
  }

  const handleBodyChange = (e: ChangeEvent<HTMLTextAreaElement>): void => {
    const el = e.currentTarget
    el.style.height = 'auto'
    el.style.height = `${Math.min(el.scrollHeight, COMPOSER_MAX_HEIGHT_PX)}px`
    setBody(el.value)
  }

  const handleSend = (): void => {
    const trimmed = body.trim()
    if (disabled || !trimmed) return

    const prompt = postTicketCommentPrompt(ticketId, trimmed)

    if (attachmentsEnabled) {
      trackSubmitted()
    }
    onSend(prompt, {
      pendingAttachments: refs,
      attachmentFiles: uploadedFiles,
    })
    setBody('')
    onSent()
  }

  return (
    <div className="flex flex-col gap-2">
      <Textarea
        value={body}
        onChange={handleBodyChange}
        placeholder="Write a comment..."
        rows={2}
        className="resize-none"
      />
      {attachmentsEnabled && (
        <AttachmentDropZone
          items={items}
          onAdd={add}
          onRetry={retry}
          onCancel={cancel}
          onRemove={remove}
          isFull={isFull}
          subtitle="Goes to support with your reply"
        />
      )}

      <div className="flex flex-col items-end gap-2">
        {isUploading && <UploadingHint id={uploadingHintId} />}
        <div className="flex items-center justify-end gap-2">
          <Button
            variant="secondary"
            size="sm"
            onClick={hasDraft ? () => setConfirmingDiscard(true) : onCancel}
          >
            {hasDraft ? 'Cancel' : 'Back'}
          </Button>
          <Button
            variant="primary"
            size="sm"
            disabled={disabled || !body.trim() || isUploading}
            onClick={handleSend}
            aria-describedby={isUploading ? uploadingHintId : undefined}
          >
            Send
          </Button>
        </div>
      </div>
      <AlertDialog open={confirmingDiscard}>
        <AlertDialog.Content className="max-w-sm z-10000">
          <AlertDialog.Header>
            <AlertDialog.Title>Discard your reply?</AlertDialog.Title>
          </AlertDialog.Header>
          <AlertDialog.Description>
            Your message and any attachments will be lost.
          </AlertDialog.Description>
          <AlertDialog.Footer className="gap-2">
            <AlertDialog.Cancel onClick={() => setConfirmingDiscard(false)}>
              Keep editing
            </AlertDialog.Cancel>
            <AlertDialog.Action onClick={discardDraft}>
              Discard
            </AlertDialog.Action>
          </AlertDialog.Footer>
        </AlertDialog.Content>
      </AlertDialog>
    </div>
  )
}

interface ThreadCardProps {
  thread: MessageThreadDetail
  ticketId: number
  onSend?: SendMessage
  sendDisabled?: boolean
  onBack: () => void
}

const ThreadCard = ({
  thread,
  ticketId,
  onSend,
  sendDisabled,
  onBack,
}: ThreadCardProps) => {
  const historyRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = historyRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [thread.comments.length])

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-3 rounded-xl border border-neutral-200 bg-neutral-0 p-4">
      <div className="shrink-0">
        <Text
          variant={TypographyStyles.BODY_XS}
          color={TextColors.DISABLED}
          className="mb-0.5"
        >
          {`Ticket #${thread.ticketId}`}
        </Text>
        <Text
          variant={TypographyStyles.LABEL_MD}
          className="font-semibold leading-snug text-neutral-800"
        >
          {thread.subject}
        </Text>
      </div>

      <div className="flex shrink-0 flex-wrap gap-x-8 gap-y-2 border-b border-neutral-100 pb-3">
        <div>
          <Text
            variant={TypographyStyles.EYEBROW}
            color={TextColors.DISABLED}
            className="mb-1"
          >
            Status
          </Text>
          <Badge
            variant="secondary"
            color={STATUS_COLOR[thread.status]}
            size="sm"
            data-testid="thread-status-badge"
          >
            {STATUS_LABEL[thread.status]}
          </Badge>
        </div>
        <div>
          <Text
            variant={TypographyStyles.EYEBROW}
            color={TextColors.DISABLED}
            className="mb-1"
          >
            Updated
          </Text>
          <Text variant={TypographyStyles.BODY_SM} color={TextColors.EMPHASIS}>
            {formatShortDateTimeWithZone(thread.updatedAt)}
          </Text>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 flex-col">
        <Text
          variant={TypographyStyles.LABEL_SM}
          color={TextColors.EMPHASIS}
          className="mb-3 shrink-0 font-semibold"
        >
          Message history
        </Text>
        <div ref={historyRef} className="min-h-0 flex-1 overflow-y-auto">
          <ThreadTimeline comments={thread.comments} />
        </div>
      </div>

      {onSend && (
        <div className="flex shrink-0 flex-col gap-2 border-t border-neutral-100 pt-3">
          <ReplyComposer
            ticketId={ticketId}
            onSend={onSend}
            onSent={onBack}
            onCancel={onBack}
            disabled={sendDisabled}
          />
        </div>
      )}
    </div>
  )
}

export const ThreadView = ({
  ticketId,
  onBack,
  onSend,
  sendDisabled,
}: ThreadViewProps) => {
  const [thread, setThread] = useState<MessageThreadDetail | null>(null)
  const [loading, setLoading] = useState(true)

  const loadThread = useCallback(() => {
    let cancelled = false
    setLoading(true)
    fetchMessageThread(ticketId).then((data) => {
      if (cancelled) return
      setThread(data)
      setLoading(false)
    })
    return () => {
      cancelled = true
    }
  }, [ticketId])

  useEffect(() => loadThread(), [loadThread])

  const renderBody = (): ReactNode => {
    if (loading) {
      return (
        <Text
          variant={TypographyStyles.BODY_SM}
          color={TextColors.MUTED}
          className="py-10 text-center"
        >
          Loading…
        </Text>
      )
    }
    if (!thread) {
      return (
        <div className="flex flex-col items-center gap-2 py-10">
          <Text variant={TypographyStyles.BODY_SM} color={TextColors.MUTED}>
            We could not load this message.
          </Text>
          <Button variant="secondary" size="sm" onClick={onBack}>
            Back to messages
          </Button>
        </div>
      )
    }
    return (
      <ThreadCard
        thread={thread}
        ticketId={ticketId}
        onSend={onSend}
        sendDisabled={sendDisabled}
        onBack={onBack}
      />
    )
  }

  return (
    <div className="flex flex-1 flex-col gap-3 overflow-hidden bg-neutral-0 p-4">
      <Button
        variant="ghost"
        size="xs"
        iconLeft={faChevronLeft}
        onClick={onBack}
        aria-label="Back to messages"
        className="shrink-0 self-start text-neutral-500"
      >
        All messages
      </Button>

      {renderBody()}
    </div>
  )
}
